import { consumptionUnitFor, isMachine } from '@fleetcore/shared'
import type { LiveConnectivity, MovementState } from '@fleetcore/shared/telemetry'
import { Cpu, Gauge, HardHat, Timer, Truck, UserRound } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Badge } from '../../components/ui/badge'
import { useFormatters } from '../../i18n/format'
import { cn } from '../../lib/cn'
import type { LiveVehicleEnvelope, VehicleView } from './api-types'

/**
 * The identity strip: what this asset is, who has it, and whether we are in touch.
 *
 * The headline reading forks on the vehicle class. A road vehicle is headlined by
 * its odometer; an engin BTP by its hour meter, because that is what it is
 * serviced on, billed on and depreciated on. `usageMetricFor()` decides, not this
 * component — see usage-metrics.ts.
 */

const CONNECTIVITY_TONE: Record<LiveConnectivity, 'success' | 'warning' | 'neutral'> = {
  online: 'success',
  stale: 'warning',
  offline: 'neutral',
}

const MOVEMENT_KEY: Record<MovementState, string> = {
  parked: 'vehicles:status.stopped',
  idling: 'vehicles:status.idling',
  moving: 'vehicles:status.moving',
}

export interface VehicleHeaderProps {
  vehicle: VehicleView
  live?: LiveVehicleEnvelope
  className?: string
}

export function VehicleHeader({ vehicle, live, className }: VehicleHeaderProps) {
  const { t } = useTranslation()
  const formatters = useFormatters()
  const machine = isMachine(vehicle)

  const makeModel = [vehicle.make, vehicle.model].filter(Boolean).join(' ')
  const connectivity = live?.connectivity ?? null
  const movement = live?.state?.movementState ?? null

  return (
    <header className={cn('flex flex-col gap-4', className)}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {machine ? (
              <HardHat aria-hidden className="h-5 w-5 shrink-0 text-foreground-subtle" />
            ) : (
              <Truck aria-hidden className="h-5 w-5 shrink-0 text-foreground-subtle" />
            )}
            <h2 className="truncate text-2xl font-semibold tracking-tight text-foreground">
              {vehicle.registration}
            </h2>
          </div>

          <p className="mt-1 truncate text-sm text-foreground-muted">
            {makeModel.length > 0 ? makeModel : t('vehicles:detail.noMakeModel')}
            {vehicle.year !== null ? ` · ${vehicle.year}` : ''}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <Badge tone="accent">{t(`vehicles:class.${vehicle.vehicleClass}`)}</Badge>
            {/* The subtype is what turns "engin" into "pelle" — the difference
                between a machine a manager recognises and a category. */}
            {machine && vehicle.machineSubtype !== null ? (
              <Badge tone="neutral">{t(`vehicles:machineSubtype.${vehicle.machineSubtype}`)}</Badge>
            ) : null}
            <Badge tone={vehicle.status === 'active' ? 'success' : 'warning'}>
              {t(`vehicles:recordStatus.${vehicle.status}`)}
            </Badge>
            <Badge tone="neutral">{t(`vehicles:installType.${vehicle.installType}`)}</Badge>
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-start gap-1.5 sm:items-end">
          {connectivity === null ? (
            <Badge tone="neutral">{t('vehicles:status.unknown')}</Badge>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge tone={CONNECTIVITY_TONE[connectivity]}>
                {t(`vehicles:connectivity.${connectivity}`)}
              </Badge>
              {/* Movement is what the vehicle was doing in its newest record.
                  Connectivity is whether we are hearing it now. Two facts, two
                  badges — a truck can be "en mouvement" and "hors ligne" at once
                  while it drives through a tunnel, and merging them loses that. */}
              {movement !== null ? (
                <Badge tone={movement === 'moving' ? 'accent' : 'neutral'}>
                  {t(MOVEMENT_KEY[movement])}
                </Badge>
              ) : null}
            </div>
          )}

          <p className="text-xs text-foreground-subtle">
            {live?.lastSeenAt
              ? t('vehicles:detail.lastSeenAt', {
                  when: formatters.dateTime(live.lastSeenAt),
                })
              : t('vehicles:detail.neverSeen')}
          </p>
        </div>
      </div>

      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border pt-4 sm:grid-cols-4">
        {/* The metering basis, stated rather than implied. An engin BTP reads
            "Heures moteur · L/h"; a truck reads "Kilométrage · L/100 km". Both
            forks come from shared, so the header cannot disagree with the tiles
            below it or with the reports built on the same two functions. */}
        <Fact
          icon={machine ? Timer : Gauge}
          label={t('vehicles:detail.meter.label')}
          value={`${t(
            machine ? 'vehicles:detail.usage.engineHours' : 'vehicles:detail.usage.odometer',
          )} · ${t(
            consumptionUnitFor(vehicle.vehicleClass) === 'l_per_hour'
              ? 'common:units.lPerHour'
              : 'common:units.lPer100km',
          )}`}
        />
        <Fact
          icon={UserRound}
          label={t('vehicles:columns.driver')}
          value={live?.driver?.displayName ?? t('vehicles:detail.noDriver')}
        />
        <Fact
          icon={Cpu}
          label={t('vehicles:columns.device')}
          value={vehicle.device?.imei ?? t('vehicles:detail.noDevice')}
          mono={vehicle.device !== null}
        />
        <Fact
          icon={machine ? HardHat : Truck}
          label={t('vehicles:detail.vin')}
          value={vehicle.vin ?? t('vehicles:detail.noVin')}
          mono={vehicle.vin !== null}
        />
      </dl>
    </header>
  )
}

function Fact({
  icon: Icon,
  label,
  value,
  mono = false,
}: {
  icon: LucideIcon
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
        <Icon aria-hidden className="h-3.5 w-3.5" />
        {label}
      </dt>
      <dd
        className={cn(
          'mt-0.5 truncate text-sm text-foreground',
          mono && 'font-mono text-xs tracking-tight',
        )}
      >
        {value}
      </dd>
    </div>
  )
}
